// App-scope (post-login + community/legal/jobs) translations.
// Merged into the same `T` dictionary used by the public site, under the `app` key.
// Only interface chrome is translated here — user-entered content is never translated.

export const APP_T = {
  de: {
    common: {
      loading: "Wird geladen…", cancel: "Abbrechen", save: "Speichern", saving: "Wird gespeichert…",
      submit: "Absenden", submitting: "Wird gesendet…", search: "Suchen", close: "Schließen",
      approve: "Genehmigen", reject: "Ablehnen", revoke: "Widerrufen", remove: "Entfernen",
      dismiss: "Verwerfen", reason: "Grund", done: "Erledigt", none: "Keine", back: "Zurück",
      all: "Alle", needsAction: "Aktion nötig", updated: "aktualisiert", due: "fällig",
      status: "Status", email: "E-Mail", name: "Name", role: "Rolle", verified: "Verifiziert",
      yes: "Ja", no: "Nein", requested: "Angefragt", request: "Anfragen", failed: "Fehlgeschlagen",
      reasonRequired: "Grund erforderlich", nothingHere: "Nichts vorhanden.", years: "J.",
    },
    layout: {
      logout: "Abmelden",
      candidateBadge: "Kandidat", employerBadge: "Arbeitgeber", adminBadge: "Admin",
      candidateTabs: { overview: "Übersicht", profile: "Profil", media: "Medien & Dokumente", jobs: "Jobs", applications: "Bewerbungen", community: "Community", account: "Konto" },
      employerTabs: { search: "Talent-Suche", jobs: "Jobs", shortlist: "Merkliste", organization: "Organisation", community: "Community", account: "Konto" },
      adminTabs: { accounts: "Konten", candidates: "Kandidaten-Freigaben", employers: "Arbeitgeber-Freigaben", jobs: "Job-Freigaben", applications: "Bewerbungen", videos: "Video-Prüfung", community: "Community", backups: "Backups", requests: "Anfragen" },
    },
    statusPill: {
      approved: "Genehmigt", underReview: "In Prüfung", submitted: "Eingereicht",
      changes: "Änderungen angefragt", withdrawn: "Zurückgezogen", draft: "Entwurf",
    },
    statusMap: {
      "Withdrawn": "Zurückgezogen",
      "Approved · changes under review": "Genehmigt · Änderungen in Prüfung",
      "Approved · changes need edits": "Genehmigt · Änderungen erforderlich",
      "Approved · unsubmitted changes": "Genehmigt · nicht eingereichte Änderungen",
      "Approved": "Genehmigt",
      "Draft": "Entwurf",
      "Submitted": "Eingereicht",
      "Under Review": "In Prüfung",
      "Changes Requested": "Änderungen angefragt",
    },
    orgBanner: {
      verified: "Organisation verifiziert — Sie können den Talentpool durchsuchen.",
      pending: "Verifizierung durch Admin ausstehend. Sie können erst nach Freigabe suchen.",
      rejected: "Registrierung abgelehnt. Aktualisieren Sie Ihre Angaben und reichen Sie erneut ein.",
      revoked: "Zugang widerrufen. Bitte kontaktieren Sie den Administrator.",
      none: "Registrieren Sie Ihre Organisation, um Zugang zum Talentpool zu beantragen.",
    },
    candHome: {
      loading: "Wird geladen…", welcome: "Willkommen", welcomeFallback: "willkommen",
      aliasPre: "Alias für Arbeitgeber:", aliasPost: "· Ihr echter Name und Ihre Kontaktdaten erscheinen nie in der Suche.",
      changesTitle: "Admin hat Änderungen angefragt",
      pendingEdits: "Sie haben Änderungen in Prüfung. Arbeitgeber sehen weiterhin Ihr zuletzt genehmigtes Profil, bis ein Admin die Änderungen freigibt.",
      pendingSubmitHint: " Reichen Sie sie zur Prüfung ein, wenn sie bereit sind.",
      completeness: "Profilvollständigkeit",
      submitChanges: "Änderungen zur Prüfung einreichen", submitReview: "Zur Prüfung einreichen",
      withdraw: "Aus der Suche zurückziehen", makeVisible: "Wieder sichtbar machen",
      completeFirst: "Bitte vervollständigen Sie vor dem Einreichen:",
      editProfile: "Profil bearbeiten", editProfileSub: "Berufe, rollenspezifische Fähigkeiten, Erfahrung, Sprachen und Präferenzen.",
      mediaTitle: "Medien & Dokumente", mediaSub: "Lebenslauf, Zertifikate und Vorstellungs-/Arbeitsvideos — privat gespeichert.",
      submitted: "Zur Prüfung eingereicht", missing: "Fehlt:",
    },
    checklist: {
      identity: "Identität & Kontakt (Name, Telefon)", location: "Grobe Ortsangabe",
      professions: "Mindestens ein Beruf", role_skills: "Rollenspezifische Fähigkeiten je Beruf",
      experience: "Erfahrung & Verfügbarkeit", languages: "Mindestens eine Sprache",
      countries: "Bevorzugte Länder", cv: "Lebenslauf hochgeladen (PDF)",
    },
    stageMap: {
      "Applied": "Beworben", "Employer Screening": "Arbeitgeber-Screening", "Interview": "Interview",
      "Offer/Contract": "Angebot/Vertrag", "Documents": "Dokumente", "Training": "Training",
      "Recognition Assessment": "Anerkennungsprüfung", "ZAV Approval": "ZAV-Zustimmung", "Visa": "Visum",
      "Pre-departure": "Vorbereitung Ausreise", "Arrival & Integration": "Ankunft & Integration",
      "Completed": "Abgeschlossen", "Declined": "Abgelehnt", "Withdrawn": "Zurückgezogen",
    },
    respMap: { employer: "Arbeitgeber", candidate: "Kandidat", authority: "Behörde", admin: "Admin" },
    catMap: { "Horse Life": "Pferdeleben", "Fairs & Events": "Messen & Events", "Horse News": "Pferde-News", "Incidents & Safety": "Vorfälle & Sicherheit", "Best Practices": "Best Practices", "Learning": "Lernen", "Jobs": "Jobs" },
    profile: {
      title: "Profil-Editor", completeness: "Vollständigkeit", multiProf: "· ein Kandidat, mehrere Berufe.",
      saved: "Profil gespeichert",
      locked: "Während der Prüfung gesperrt. Ziehen Sie die Einreichung im Tab „Übersicht“ zurück, um zu bearbeiten.",
      secIdentity: "Identität & Kontakt (privat)", secIdentityHint: "Nie an Arbeitgeber gezeigt. Nur zur Verifizierung und Plattformkontakt.",
      legalName: "Vollständiger Name", phone: "Telefon", exactAddress: "Genaue Adresse (privat)",
      secLocation: "Ort & Verfügbarkeit",
      broadLocation: "Grobe Ortsangabe (für Arbeitgeber sichtbar)", broadLocationHint: "z. B. Bayern, Deutschland — nicht Ihre genaue Adresse.",
      experience: "Jahre Erfahrung", availability: "Verfügbarkeit", select: "Auswählen…",
      availabilityOpts: { Immediate: "Sofort", "Within 1 month": "Innerhalb 1 Monat", "Within 3 months": "Innerhalb 3 Monate", Flexible: "Flexibel" },
      secProfessions: "Berufe & rollenspezifische Fähigkeiten", professionsHint: "Wählen Sie einen oder mehrere. Jeder Beruf behält seine eigenen Fähigkeiten.",
      skillsSuffix: "Fähigkeiten",
      secLanguages: "Sprachen & Präferenzen", languages: "Sprachen", languagePlaceholder: "Sprache",
      levels: { Basic: "Grundkenntnisse", Conversational: "Konversationssicher", B2: "B2", Fluent: "Fließend", Native: "Muttersprache" },
      preferredCountries: "Bevorzugte Länder", addCountry: "Land hinzufügen",
      training: "Ausbildung / Zertifikate (Zusammenfassung)", headline: "Überschrift", bio: "Kurzbiografie",
    },
    media: {
      title: "Medien & Dokumente",
      sub: "Dateien werden privat gespeichert — Arbeitgeber erhalten nie einen direkten Link oder Ihren Original-Lebenslauf. Limits werden angezeigt und serverseitig durchgesetzt.",
      upload: "Hochladen", uploading: "Wird hochgeladen…", noFiles: "Noch keine Dateien.", openPdf: "PDF öffnen",
      uploaded: "hochgeladen", deleteFailed: "Löschen fehlgeschlagen",
      unsupported: "Nicht unterstützter Typ. Erlaubt:", exceeds: "überschreitet", yoursIs: "Ihre ist",
      maxFiles: "Max. {n} Dateien. Löschen Sie zuerst eine.", videoIs: "Video ist", limitIs: "Limit ist",
      rules: {
        cv: { label: "Lebenslauf", note: "Ein aktives PDF, max. 5 MB." },
        certificate: { label: "Zertifikate & Dokumente", note: "PDF/JPG/PNG, max. 10 MB je Datei, bis zu 15 Dateien." },
        intro_video: { label: "Vorstellungsvideo", note: "MP4/MOV, ≤ 90 s, ≤ 100 MB." },
        work_video: { label: "Arbeitsvideos", note: "MP4/MOV, ≤ 120 s, ≤ 150 MB, bis zu 3." },
      },
    },
    candJobs: {
      title: "Jobs",
      sub: "Genehmigte Stellen. Sie können sich bewerben, sobald Ihr Profil vom Admin freigegeben ist und Sie die Pflichtkriterien erfüllen.",
      empty: "Derzeit keine offenen Stellen.", viewApply: "Ansehen & bewerben",
      mandatory: "Pflichtkriterien", preferred: "Bevorzugt", noneWord: "Keine",
      eligibleYes: "Sie erfüllen alle Pflichtkriterien.", notEligible: "Noch nicht berechtigt:",
      alreadyApplied: "Bereits beworben", applying: "Wird gesendet…", applyNow: "Jetzt bewerben",
      submitted: "Bewerbung eingereicht", missingPrefix: "Fehlt:", signinAsCandidate: "Als Kandidat anmelden",
    },
    candApps: {
      title: "Meine Bewerbungen",
      sub: "Jede Bewerbung verfolgt ihre eigenen Phasen, die zuständige Partei und die nächste Aktion.",
      empty: "Sie haben sich noch auf keine Stelle beworben.", next: "Nächstes:",
    },
    community: {
      title: "Community",
      sub: "Ein moderierter Raum für die Pferdewelt. Recruiter verbinden sich mit Kandidaten nur über den begleiteten Prozess — nie über Kontaktdaten in Beiträgen.",
      myDashboard: "Mein Dashboard", signin: "Anmelden", join: "Mitmachen",
      newPost: "Neuer Beitrag", signinToPost: "Zum Posten anmelden",
      all: "Alle", emptyCanPost: "Noch keine Beiträge in dieser Kategorie. Seien Sie die/der Erste.",
      emptyGuest: "Noch keine Beiträge in dieser Kategorie. Melden Sie sich an, um die Unterhaltung zu starten.",
      awaitingReview: "Wird geprüft", awaitingReviewSub: "Medien werden vor der Anzeige auf Kontaktdaten geprüft",
      candidate: "Kandidat", member: "Mitglied",
      loadingComments: "Wird geladen…", noComments: "Noch keine Kommentare.",
      addComment: "Kommentar hinzufügen (keine Kontaktdaten)…",
      reported: "An Moderatoren gemeldet", report: "Melden",
      pinned: "Angeheftet", verifiedResource: "Verifizierte Ressource",
      commentOne: "Kommentar", commentOther: "Kommentare",
      shareTitle: "Mit der Community teilen", titlePlaceholder: "Titel",
      bodyPlaceholder: "Teilen Sie Tipps, News oder ein Event — bitte keine Telefonnummern, E-Mails oder Social-Media-Handles.",
      addMedia: "Foto/Video hinzufügen", mediaHint: "· Bilder ≤ 10 MB, Video ≤ 150 MB/120 s",
      titleRequired: "Ein Titel ist erforderlich.", posted: "In der Community veröffentlicht",
      posting: "Wird gepostet…", post: "Posten",
    },
    legal: { home: "Startseite", loading: "Wird geladen…", notFound: "Dokument nicht gefunden.", version: "Version" },
    account: {
      title: "Konto & Datenschutz",
      sub: "Verwalten Sie Ihre Einwilligung und Datenschutzanfragen. Nichts wird automatisch gelöscht — ein Administrator prüft und bearbeitet jede Anfrage.",
      yourConsent: "Ihre Einwilligung", acceptedOn: "Akzeptiert am", versionsWord: "Versionen",
      noConsent: "Kein Einwilligungsdatensatz gefunden.",
      terms: "Nutzungsbedingungen", privacy: "Datenschutzrichtlinie", guidelines: "Community-Richtlinien",
      requestSubmitted: "Anfrage gesendet — ein Administrator wird sie bearbeiten.",
      yourRequests: "Ihre Anfragen", noRequests: "Noch keine Anfragen.",
      cards: {
        data_export: { title: "Datenexport anfordern", desc: "Erhalten Sie eine Kopie der personenbezogenen Daten, die wir über Sie speichern." },
        consent_withdrawal: { title: "Einwilligung widerrufen", desc: "Widerrufen Sie Ihre Zustimmung zu unseren Nutzungsbedingungen & Datenschutzrichtlinie." },
        account_deletion: { title: "Kontolöschung anfordern", desc: "Bitten Sie uns, Ihr Konto und zugehörige Daten zu löschen." },
      },
      labels: { data_export: "Datenexport", account_deletion: "Kontolöschung", consent_withdrawal: "Einwilligungswiderruf" },
    },
    notif: { title: "Benachrichtigungen", markAll: "Alle als gelesen markieren", empty: "Sie sind auf dem neuesten Stand." },
    notifTpl: {
      titles: {
        "Your profile was approved": "Ihr Profil wurde genehmigt",
        "Changes requested on your profile": "Änderungen an Ihrem Profil angefragt",
        "Profile approval revoked": "Profilfreigabe widerrufen",
        "Job approved": "Stelle genehmigt",
        "Job needs changes": "Stelle benötigt Änderungen",
      },
      bodies: {
        "You're now visible to verified employers and can apply to jobs.": "Sie sind jetzt für verifizierte Arbeitgeber sichtbar und können sich auf Stellen bewerben.",
      },
      appUpdate: "Bewerbungs-Update",
      appMoved: "Ihre Bewerbung wurde zu {stage} verschoben.",
    },
    empOrg: {
      title: "Organisation",
      sub: "Registrieren Sie Ihre Organisation und autorisierte Kontaktperson. Ein Admin muss Sie verifizieren, bevor Sie auf den Talentpool zugreifen können.",
      submitted: "Organisation zur Verifizierung eingereicht",
      orgName: "Name der Organisation", regNumber: "Registernummer", country: "Land", website: "Website",
      contactName: "Name der autorisierten Kontaktperson", contactRole: "Funktion der Kontaktperson", contactPhone: "Telefon der Kontaktperson",
      submitFor: "Zur Verifizierung einreichen", updateResubmit: "Aktualisieren & erneut einreichen",
    },
    empSearch: {
      title: "Talent-Suche", approvedOnly: "Nur genehmigte Profile · maskiert",
      anyProfession: "Beliebiger Beruf", skill: "Fähigkeit", language: "Sprache",
      country: "Bevorzugtes Land", keyword: "Stichwort", search: "Suchen",
      noMatch: "Keine genehmigten Kandidaten passen.", view: "Ansehen",
      videoPreviews: "Videovorschau", certificates: "Zertifikate",
      certDisabled: "Zertifikat(e) vorhanden. Vorschauen sind deaktiviert, bis Kennungen maskiert und geprüft werden können.",
      privacyNote: "Kein echter Name, keine Telefonnummer, E-Mail, genaue Adresse oder Original-Lebenslauf wird angezeigt. Zugriffe werden protokolliert und begrenzt.",
      addShortlist: "Zur Merkliste hinzufügen", addedShortlist: "Zur Merkliste hinzugefügt",
      languagesWord: "Sprachen",
    },
    empJobs: {
      title: "Jobs", postNew: "Neue Stelle ausschreiben", titlePlaceholder: "Titel",
      profession: "Beruf", location: "Ort", description: "Beschreibung",
      mandatoryPerVacancy: "Pflichtkriterien (pro Stelle)", createDraft: "Entwurf erstellen",
      yourJobs: "Ihre Stellen", noJobs: "Noch keine Stellen.",
      reasonPrefix: "Grund:", submit: "Einreichen", applicants: "Bewerber",
      noApplicants: "Noch keine Bewerber.",
      immigrationNote: "Einwanderungs-/Vermittlungsphasen (Visum, ZAV usw.) werden vom Admin gesteuert.",
      titleRequired: "Titel erforderlich", jobCreated: "Stelle als Entwurf erstellt", submittedReview: "Zur Prüfung eingereicht",
      stageSet: "Phase:", value: "Wert",
      empStages: { "Employer Screening": "Arbeitgeber-Screening", Interview: "Interview", "Offer/Contract": "Angebot/Vertrag", Declined: "Abgelehnt" },
    },
    empShortlist: {
      title: "Merkliste", empty: "Noch keine Kandidaten auf der Merkliste.",
    },
    adminHome: {
      title: "Konten",
      seedNote: "Admin-Zugang wird sicher aus Umgebungsvariablen initialisiert und vorab verifiziert. Nutzen Sie ",
      seedNoteBold: "Kandidaten-Freigaben", seedNotePost: ", um eingereichte Profile zu prüfen.",
      allAccounts: "Alle Konten", usersSuffix: "Nutzer", noUsers: "Noch keine Nutzer.", failedLoad: "Laden fehlgeschlagen",
      colName: "Name", colEmail: "E-Mail", colRole: "Rolle", colVerified: "Verifiziert",
    },
    adminCand: {
      title: "Kandidaten-Freigaben", needsAction: "Aktion nötig", all: "Alle",
      colAlias: "Alias", colCandidate: "Kandidat", colProfessions: "Berufe", colStatus: "Status",
      review: "Prüfen", pendingChanges: "Ausstehende Änderungen an:", pendingChangesPost: ". Arbeitgeber sehen weiterhin die zuletzt genehmigte Version.",
      privateAdmin: "Privat (nur Admin)", recruiterVisible: "Für Recruiter sichtbar",
      roleSkills: "Rollenspezifische Fähigkeiten", evidenceMedia: "Nachweise & Medien", noFilesShort: "Keine Dateien.",
      reasonLabel: "Grund (erforderlich, um Änderungen anzufordern oder zu widerrufen)",
      reasonPlaceholder: "Erklären Sie, was geändert werden muss…",
      approve: "Genehmigen", requestChanges: "Änderungen anfordern", revokeApproval: "Freigabe widerrufen",
      addReason: "Grund hinzufügen", done: "Erledigt",
    },
    adminEmp: {
      title: "Arbeitgeber-Freigaben",
      contact: "Kontakt", account: "Konto", noSite: "keine Website", regPrefix: "Reg.:",
      verify: "Verifizieren", reject: "Ablehnen", revoke: "Widerrufen",
      reasonToPrefix: "Grund zum ", done: "Erledigt", reasonRequired: "Grund erforderlich",
      filters: { pending: "Ausstehend", verified: "Verifiziert", all: "Alle" },
      statuses: { pending: "ausstehend", verified: "verifiziert", rejected: "abgelehnt", revoked: "widerrufen" },
    },
    adminJobs: {
      title: "Job-Freigaben", filters: { submitted: "eingereicht", approved: "genehmigt", all: "alle" },
      mandatoryPrefix: "Pflicht:", noneWord: "keine", reasonPrefix: "Grund:",
      approve: "Genehmigen", reject: "Ablehnen", reasonToReject: "Grund zur Ablehnung…", done: "Erledigt", reasonRequired: "Grund erforderlich",
    },
    adminApps: {
      title: "Bewerbungen",
      sub: "Finden Sie Bewerbungen und verwalten Sie Phase, zuständige Partei, Fälligkeit, nächste Aktion und Verlauf jeder einzelnen.",
      searchPlaceholder: "Suche nach Kandidaten-Alias, Stelle oder Arbeitgeber…", allStages: "Alle Phasen", search: "Suchen",
      noMatch: "Keine Bewerbungen passen.",
      candidateWord: "Kandidat", statusWord: "Status", responsibleWord: "zuständig:", next: "Nächstes:",
      manageHistory: "Verwalten & Verlauf", hide: "Ausblenden",
      stage: "Phase", responsibleParty: "Zuständige Partei", nextActionNote: "Nächste Aktion / Notiz",
      nextPlaceholder: "Was passiert als Nächstes…", dueDate: "Fälligkeitsdatum",
      updateStage: "Phase aktualisieren", conditional: "(bedingt)",
      conditionalNote: "Bedingte Phasen (Anerkennung, ZAV, Visum) sind optional — nur setzen, wenn der Fall es erfordert.",
      stageSetTo: "Phase gesetzt auf", byWord: "von",
    },
    adminComm: {
      title: "Community-Moderation",
      tabs: { reports: "Meldungen", media: "Medien-Prüfung", posts: "Alle Beiträge" },
      noReports: "Keine offenen Meldungen.", reportedPrefix: "Gemeldet:", noReasonGiven: "kein Grund angegeben",
      contentRemoved: "(Inhalt bereits entfernt)", removeContent: "Inhalt entfernen", dismissReport: "Meldung verwerfen",
      contentRemovedToast: "Inhalt entfernt", reportDismissed: "Meldung verworfen", failed: "Fehlgeschlagen",
      byWord: "von", reportsSuffix: "Meldungen",
      pinVerified: "Als verifiziert anheften", unpin: "Lösen abheften",
      mediaHint: "Prüfen Sie jedes Bild/Video auf sichtbare oder gesprochene Kontaktdaten, bevor es öffentlich wird. Nur genehmigte Medien werden im öffentlichen Feed geladen (direkte URLs bleiben bis zur Freigabe gesperrt).",
      filters: { pending: "ausstehend", approved: "genehmigt", rejected: "abgelehnt" },
      noMedia: "Keine Medien", postPrefix: "Beitrag:", untitled: "(ohne Titel)",
      statusWord: "Status", approve: "Genehmigen", reject: "Ablehnen",
      rejectPlaceholder: "Grund (z. B. Telefonnummer auf Schild sichtbar)…", confirmReject: "Ablehnung bestätigen",
      mediaPublished: "Medien veröffentlicht", mediaRejected: "Medien abgelehnt", noteRequired: "Ein Grund ist erforderlich",
      visible: "sichtbar",
    },
    adminVideo: {
      title: "Video-Prüfung", filters: { pending: "ausstehend", approved: "genehmigt", rejected: "abgelehnt" },
      hint: "Prüfen Sie jedes Video auf gesprochene oder eingeblendete Kontaktdaten, bevor es für Arbeitgeber sichtbar wird. Nur genehmigte Videos werden freigegeben.",
      noVideos: "Keine Videos", statusWord: "Status", approve: "Genehmigen", reject: "Ablehnen",
      rejectPlaceholder: "Grund (z. B. gesprochene Telefonnummer bei 0:12)…", confirmReject: "Ablehnung bestätigen",
      notePrefix: "Notiz:", videoReleased: "Video für Arbeitgeber freigegeben", videoRejected: "Video abgelehnt", noteRequired: "Ein Grund ist erforderlich",
      kinds: { intro_video: "Vorstellungsvideo", work_video: "Arbeitsvideo" },
    },
    adminBackups: {
      title: "Backups & Wiederherstellung", runNow: "Backup jetzt ausführen",
      note1: "Tägliches automatisches Backup (02:00 UTC) speichert einen DB-Dump + privates Medienarchiv im Objektspeicher (letzte ",
      note2: " werden aufbewahrt). Das übersteht Pod-Neustarts, ist aber ",
      noteNot: "keine",
      note3: " unabhängige Off-Site-DR-Kopie — der Objektspeicher hat kein physisches Löschen, daher ist die Aufbewahrung logisch. Der Wiederherstellungstest schreibt nur in ",
      note4: ". Siehe ",
      showArchived: "Archivierte / verworfene anzeigen",
      noBackups: "Noch keine Backups. Führen Sie jetzt eines aus.",
      runRestoreTest: "Wiederherstellungstest ausführen", running: "Läuft…",
      unarchive: "Dearchivieren", dismiss: "Verwerfen",
      mediaWord: "Medien", collectionsWord: "Sammlungen",
      restoreStarted: "Backup gestartet — wird in Kürze aktualisiert",
      restoreTestStarted: "Wiederherstellungstest gestartet",
      restoreVerified: "Wiederherstellung verifiziert — vollständig wiederherstellbar",
      restoreRan: "Wiederherstellung ausgeführt — siehe Bericht",
      restoreFailed: "Wiederherstellung fehlgeschlagen: ",
      alreadyRunning: "Ein Wiederherstellungstest läuft bereits. Bitte warten.",
      fullyRecoverable: "Vollständig wiederherstellbar:", recordsMatch: "Datensätze stimmen überein:",
      mediaRestored: "Medien wiederhergestellt:", sampleOpened: "Stichprobe geöffnet:",
      missingFiles: "fehlende Datei(en) in diesem Backup — nicht vollständig wiederherstellbar.",
      integrity: "Medienintegrität:", presentWord: "vorhanden", missingWord: "fehlend", unverifiedWord: "ungeprüft",
      employerVisible: "arbeitgeber-sichtbar", errorPrefix: "Fehler:",
      statuses: { active: "aktiv", failed: "fehlgeschlagen", incomplete: "unvollständig", archived: "archiviert" },
    },
    adminReqs: {
      title: "Konto- & Datenschutzanfragen",
      filters: { open: "offen", resolved: "erledigt", all: "alle" },
      sub: "Löschungs-, Export- und Einwilligungswiderrufsanfragen werden manuell bearbeitet und protokolliert. Es werden keine Daten automatisch entfernt.",
      noRequests: "Keine Anfragen", markResolved: "Als erledigt markieren", resolvedWord: "erledigt",
      requestResolved: "Anfrage erledigt",
      labels: { account_deletion: "Kontolöschung", data_export: "Datenexport", consent_withdrawal: "Einwilligungswiderruf" },
    },
  },

  en: {
    common: {
      loading: "Loading…", cancel: "Cancel", save: "Save", saving: "Saving…",
      submit: "Submit", submitting: "Submitting…", search: "Search", close: "Close",
      approve: "Approve", reject: "Reject", revoke: "Revoke", remove: "Remove",
      dismiss: "Dismiss", reason: "Reason", done: "Done", none: "None", back: "Back",
      all: "All", needsAction: "Needs action", updated: "updated", due: "due",
      status: "Status", email: "Email", name: "Name", role: "Role", verified: "Verified",
      yes: "Yes", no: "No", requested: "Requested", request: "Request", failed: "Failed",
      reasonRequired: "Reason required", nothingHere: "Nothing here.", years: "yrs",
    },
    layout: {
      logout: "Logout",
      candidateBadge: "Candidate", employerBadge: "Employer", adminBadge: "Admin",
      candidateTabs: { overview: "Overview", profile: "Profile", media: "Media & Documents", jobs: "Jobs", applications: "Applications", community: "Community", account: "Account" },
      employerTabs: { search: "Talent Search", jobs: "Jobs", shortlist: "Shortlist", organization: "Organization", community: "Community", account: "Account" },
      adminTabs: { accounts: "Accounts", candidates: "Candidate Approvals", employers: "Employer Approvals", jobs: "Job Approvals", applications: "Applications", videos: "Video Review", community: "Community", backups: "Backups", requests: "Requests" },
    },
    statusPill: {
      approved: "Approved", underReview: "Under review", submitted: "Submitted",
      changes: "Changes requested", withdrawn: "Withdrawn", draft: "Draft",
    },
    statusMap: {
      "Withdrawn": "Withdrawn",
      "Approved · changes under review": "Approved · changes under review",
      "Approved · changes need edits": "Approved · changes need edits",
      "Approved · unsubmitted changes": "Approved · unsubmitted changes",
      "Approved": "Approved",
      "Draft": "Draft",
      "Submitted": "Submitted",
      "Under Review": "Under Review",
      "Changes Requested": "Changes Requested",
    },
    orgBanner: {
      verified: "Organization verified — you can search the talent pool.",
      pending: "Pending admin verification. You can't search candidates until approved.",
      rejected: "Registration rejected. Update your details and re-submit.",
      revoked: "Access revoked. Contact the administrator.",
      none: "Register your organization to request talent-pool access.",
    },
    candHome: {
      loading: "Loading…", welcome: "Welcome", welcomeFallback: "there",
      aliasPre: "Alias shown to employers:", aliasPost: "· Your legal name and contacts are never shown in search.",
      changesTitle: "Admin requested changes",
      pendingEdits: "You have edits awaiting review. Employers still see your last approved profile until an admin approves the changes.",
      pendingSubmitHint: " Submit them for review when ready.",
      completeness: "Profile completeness",
      submitChanges: "Submit changes for review", submitReview: "Submit for review",
      withdraw: "Withdraw from search", makeVisible: "Make visible again",
      completeFirst: "Complete these before submitting:",
      editProfile: "Edit profile", editProfileSub: "Professions, role-specific skills, experience, languages and preferences.",
      mediaTitle: "Media & documents", mediaSub: "CV, certificates and intro/work videos — stored privately.",
      submitted: "Submitted for admin review", missing: "Missing:",
    },
    checklist: {
      identity: "Identity & contact (name, phone)", location: "Broad location",
      professions: "At least one profession", role_skills: "Role-specific skills for each profession",
      experience: "Experience & availability", languages: "At least one language",
      countries: "Preferred countries", cv: "CV uploaded (PDF)",
    },
    stageMap: {
      "Applied": "Applied", "Employer Screening": "Employer Screening", "Interview": "Interview",
      "Offer/Contract": "Offer/Contract", "Documents": "Documents", "Training": "Training",
      "Recognition Assessment": "Recognition Assessment", "ZAV Approval": "ZAV Approval", "Visa": "Visa",
      "Pre-departure": "Pre-departure", "Arrival & Integration": "Arrival & Integration",
      "Completed": "Completed", "Declined": "Declined", "Withdrawn": "Withdrawn",
    },
    respMap: { employer: "Employer", candidate: "Candidate", authority: "Authority", admin: "Admin" },
    catMap: { "Horse Life": "Horse Life", "Fairs & Events": "Fairs & Events", "Horse News": "Horse News", "Incidents & Safety": "Incidents & Safety", "Best Practices": "Best Practices", "Learning": "Learning", "Jobs": "Jobs" },
    profile: {
      title: "Profile editor", completeness: "Completeness", multiProf: "· one candidate, multiple professions.",
      saved: "Profile saved",
      locked: "Locked while under review. Withdraw the submission from the Overview tab to edit.",
      secIdentity: "Identity & contact (private)", secIdentityHint: "Never shown to employers. Used only for verification and platform contact.",
      legalName: "Full legal name", phone: "Phone", exactAddress: "Exact address (private)",
      secLocation: "Location & availability",
      broadLocation: "Broad location (shown to employers)", broadLocationHint: "e.g. Bavaria, Germany — not your exact address.",
      experience: "Years of experience", availability: "Availability", select: "Select…",
      availabilityOpts: { Immediate: "Immediate", "Within 1 month": "Within 1 month", "Within 3 months": "Within 3 months", Flexible: "Flexible" },
      secProfessions: "Professions & role-specific skills", professionsHint: "Choose one or more. Each profession keeps its own skills.",
      skillsSuffix: "skills",
      secLanguages: "Languages & preferences", languages: "Languages", languagePlaceholder: "Language",
      levels: { Basic: "Basic", Conversational: "Conversational", B2: "B2", Fluent: "Fluent", Native: "Native" },
      preferredCountries: "Preferred countries", addCountry: "Add country",
      training: "Training / certifications summary", headline: "Headline", bio: "Short bio",
    },
    media: {
      title: "Media & documents",
      sub: "Files are stored privately — employers never receive a direct link or your original CV. Limits are shown and enforced on the server.",
      upload: "Upload", uploading: "Uploading…", noFiles: "No files yet.", openPdf: "Open PDF",
      uploaded: "uploaded", deleteFailed: "Delete failed",
      unsupported: "Unsupported type. Allowed:", exceeds: "exceeds", yoursIs: "yours is",
      maxFiles: "Max {n} files. Delete one first.", videoIs: "Video is", limitIs: "limit is",
      rules: {
        cv: { label: "CV", note: "One active PDF, max 5 MB." },
        certificate: { label: "Certificates & documents", note: "PDF/JPG/PNG, max 10 MB each, up to 15 files." },
        intro_video: { label: "Intro video", note: "MP4/MOV, ≤ 90s, ≤ 100 MB." },
        work_video: { label: "Work demonstration videos", note: "MP4/MOV, ≤ 120s, ≤ 150 MB, up to 3." },
      },
    },
    candJobs: {
      title: "Jobs",
      sub: "Approved vacancies. You can apply once your profile is admin-approved and you meet the mandatory criteria.",
      empty: "No open jobs right now.", viewApply: "View & apply",
      mandatory: "Mandatory criteria", preferred: "Preferred", noneWord: "None",
      eligibleYes: "You meet all mandatory criteria.", notEligible: "Not eligible yet:",
      alreadyApplied: "Already applied", applying: "Applying…", applyNow: "Apply now",
      submitted: "Application submitted", missingPrefix: "Missing:", signinAsCandidate: "Sign in as a candidate",
    },
    candApps: {
      title: "My Applications",
      sub: "Each application tracks its own stages, responsible party and next action.",
      empty: "You haven't applied to any jobs yet.", next: "Next:",
    },
    community: {
      title: "Community",
      sub: "A moderated space for the equestrian world. Recruiters connect with candidates only through the guided journey — never via contact details in posts.",
      myDashboard: "My dashboard", signin: "Sign in", join: "Join",
      newPost: "New post", signinToPost: "Sign in to post",
      all: "All", emptyCanPost: "No posts in this category yet. Be the first to share.",
      emptyGuest: "No posts in this category yet. Sign in to start the conversation.",
      awaitingReview: "Awaiting review", awaitingReviewSub: "Media is checked for contact details before it's shown",
      candidate: "Candidate", member: "Member",
      loadingComments: "Loading…", noComments: "No comments yet.",
      addComment: "Add a comment (no contact details)…",
      reported: "Reported to moderators", report: "Report",
      pinned: "Pinned", verifiedResource: "Verified resource",
      commentOne: "comment", commentOther: "comments",
      shareTitle: "Share with the community", titlePlaceholder: "Title",
      bodyPlaceholder: "Share tips, news or an event — no phone numbers, emails or social handles please.",
      addMedia: "Add photo/video", mediaHint: "· images ≤10MB, video ≤150MB/120s",
      titleRequired: "A title is required.", posted: "Posted to the community",
      posting: "Posting…", post: "Post",
    },
    legal: { home: "Home", loading: "Loading…", notFound: "Document not found.", version: "Version" },
    account: {
      title: "Account & Privacy",
      sub: "Manage your consent and privacy requests. Nothing is deleted automatically — an administrator reviews and actions every request.",
      yourConsent: "Your consent", acceptedOn: "Accepted on", versionsWord: "versions",
      noConsent: "No consent record found.",
      terms: "Terms", privacy: "Privacy Policy", guidelines: "Community Guidelines",
      requestSubmitted: "Request submitted — an administrator will action it.",
      yourRequests: "Your requests", noRequests: "No requests yet.",
      cards: {
        data_export: { title: "Request a data export", desc: "Get a copy of the personal data we hold about you." },
        consent_withdrawal: { title: "Withdraw consent", desc: "Withdraw your consent to our Terms & Privacy Policy." },
        account_deletion: { title: "Request account deletion", desc: "Ask us to delete your account and associated data." },
      },
      labels: { data_export: "Data export", account_deletion: "Account deletion", consent_withdrawal: "Consent withdrawal" },
    },
    notif: { title: "Notifications", markAll: "Mark all read", empty: "You're all caught up." },
    notifTpl: {
      titles: {
        "Your profile was approved": "Your profile was approved",
        "Changes requested on your profile": "Changes requested on your profile",
        "Profile approval revoked": "Profile approval revoked",
        "Job approved": "Job approved",
        "Job needs changes": "Job needs changes",
      },
      bodies: {
        "You're now visible to verified employers and can apply to jobs.": "You're now visible to verified employers and can apply to jobs.",
      },
      appUpdate: "Application update",
      appMoved: "Your application moved to {stage}.",
    },
    empOrg: {
      title: "Organization",
      sub: "Register your organization and authorized contact. An admin must verify you before you can access the talent pool.",
      submitted: "Organization submitted for verification",
      orgName: "Organization name", regNumber: "Registration number", country: "Country", website: "Website",
      contactName: "Authorized contact name", contactRole: "Contact role", contactPhone: "Contact phone",
      submitFor: "Submit for verification", updateResubmit: "Update & re-submit",
    },
    empSearch: {
      title: "Talent Search", approvedOnly: "Approved profiles only · masked",
      anyProfession: "Any profession", skill: "Skill", language: "Language",
      country: "Preferred country", keyword: "Keyword", search: "Search",
      noMatch: "No approved candidates match.", view: "View",
      videoPreviews: "Video previews", certificates: "Certificates",
      certDisabled: "certificate(s) on file. Previews are disabled until identifiers can be masked and reviewed.",
      privacyNote: "No legal name, phone, email, exact address or original CV is shown. Views are logged and rate-limited.",
      addShortlist: "Add to shortlist", addedShortlist: "Added to shortlist",
      languagesWord: "Languages",
    },
    empJobs: {
      title: "Jobs", postNew: "Post a new job", titlePlaceholder: "Title",
      profession: "Profession", location: "Location", description: "Description",
      mandatoryPerVacancy: "Mandatory criteria (per-vacancy)", createDraft: "Create draft",
      yourJobs: "Your jobs", noJobs: "No jobs yet.",
      reasonPrefix: "Reason:", submit: "Submit", applicants: "Applicants",
      noApplicants: "No applicants yet.",
      immigrationNote: "Immigration/placement stages (Visa, ZAV, etc.) are admin-controlled.",
      titleRequired: "Title required", jobCreated: "Job created as draft", submittedReview: "Submitted for review",
      stageSet: "Stage:", value: "value",
      empStages: { "Employer Screening": "Employer Screening", Interview: "Interview", "Offer/Contract": "Offer/Contract", Declined: "Declined" },
    },
    empShortlist: {
      title: "Shortlist", empty: "No candidates shortlisted yet.",
    },
    adminHome: {
      title: "Accounts",
      seedNote: "Admin access is seeded securely from environment variables and pre-verified. Use ",
      seedNoteBold: "Candidate Approvals", seedNotePost: " to review submitted profiles.",
      allAccounts: "All accounts", usersSuffix: "users", noUsers: "No users yet.", failedLoad: "Failed to load",
      colName: "Name", colEmail: "Email", colRole: "Role", colVerified: "Verified",
    },
    adminCand: {
      title: "Candidate Approvals", needsAction: "Needs action", all: "All",
      colAlias: "Alias", colCandidate: "Candidate", colProfessions: "Professions", colStatus: "Status",
      review: "Review", pendingChanges: "Pending changes to:", pendingChangesPost: ". Employers still see the last approved version.",
      privateAdmin: "Private (admin only)", recruiterVisible: "Recruiter-visible",
      roleSkills: "Role-specific skills", evidenceMedia: "Evidence & media", noFilesShort: "No files.",
      reasonLabel: "Reason (required to request changes or revoke)",
      reasonPlaceholder: "Explain what needs to change…",
      approve: "Approve", requestChanges: "Request changes", revokeApproval: "Revoke approval",
      addReason: "Add a reason", done: "Done",
    },
    adminEmp: {
      title: "Employer Approvals",
      contact: "Contact", account: "Account", noSite: "no site", regPrefix: "Reg:",
      verify: "Verify", reject: "Reject", revoke: "Revoke",
      reasonToPrefix: "Reason to ", done: "Done", reasonRequired: "Reason required",
      filters: { pending: "Pending", verified: "Verified", all: "All" },
      statuses: { pending: "pending", verified: "verified", rejected: "rejected", revoked: "revoked" },
    },
    adminJobs: {
      title: "Job Approvals", filters: { submitted: "submitted", approved: "approved", all: "all" },
      mandatoryPrefix: "Mandatory:", noneWord: "none", reasonPrefix: "Reason:",
      approve: "Approve", reject: "Reject", reasonToReject: "Reason to reject…", done: "Done", reasonRequired: "Reason required",
    },
    adminApps: {
      title: "Applications",
      sub: "Find applications and manage each one's stage, responsible party, due date, next action and history.",
      searchPlaceholder: "Search by candidate alias, job or employer…", allStages: "All stages", search: "Search",
      noMatch: "No applications match.",
      candidateWord: "Candidate", statusWord: "status", responsibleWord: "responsible:", next: "Next:",
      manageHistory: "Manage & history", hide: "Hide",
      stage: "Stage", responsibleParty: "Responsible party", nextActionNote: "Next action / note",
      nextPlaceholder: "What happens next…", dueDate: "Due date",
      updateStage: "Update stage", conditional: "(conditional)",
      conditionalNote: "Conditional stages (recognition, ZAV, visa) are optional — set only when the case requires them.",
      stageSetTo: "Stage set to", byWord: "by",
    },
    adminComm: {
      title: "Community Moderation",
      tabs: { reports: "Reports", media: "Media review", posts: "All posts" },
      noReports: "No open reports.", reportedPrefix: "Reported", noReasonGiven: "no reason given",
      contentRemoved: "(content already removed)", removeContent: "Remove content", dismissReport: "Dismiss report",
      contentRemovedToast: "Content removed", reportDismissed: "Report dismissed", failed: "Failed",
      byWord: "by", reportsSuffix: "reports",
      pinVerified: "Pin as verified", unpin: "Unpin",
      mediaHint: "Review each image/video for visible or spoken contact details before it becomes public. Only approved media loads on the public feed (direct URLs stay blocked until approved).",
      filters: { pending: "pending", approved: "approved", rejected: "rejected" },
      noMedia: "No media", postPrefix: "Post:", untitled: "(untitled)",
      statusWord: "status", approve: "Approve", reject: "Reject",
      rejectPlaceholder: "Reason (e.g. phone number visible on sign)…", confirmReject: "Confirm reject",
      mediaPublished: "Media published", mediaRejected: "Media rejected", noteRequired: "A note is required",
      visible: "visible",
    },
    adminVideo: {
      title: "Video Review", filters: { pending: "pending", approved: "approved", rejected: "rejected" },
      hint: "Review each video for spoken or on-screen contact details before it becomes visible to employers. Only approved videos are released.",
      noVideos: "No videos", statusWord: "status", approve: "Approve", reject: "Reject",
      rejectPlaceholder: "Reason (e.g. spoken phone number at 0:12)…", confirmReject: "Confirm reject",
      notePrefix: "Note:", videoReleased: "Video released to employers", videoRejected: "Video rejected", noteRequired: "A note is required",
      kinds: { intro_video: "Intro video", work_video: "Work demo video" },
    },
    adminBackups: {
      title: "Backups & Restore", runNow: "Run backup now",
      note1: "Daily automated backup (02:00 UTC) stores a DB dump + private media archive in object storage (keep last ",
      note2: "). This survives pod restarts but is ",
      noteNot: "not",
      note3: " an independent off-site DR copy — object storage has no physical delete, so retention is logical. Restore-test writes into ",
      note4: " only. See ",
      showArchived: "Show archived / dismissed",
      noBackups: "No backups yet. Run one now.",
      runRestoreTest: "Run restore test", running: "Running…",
      unarchive: "Unarchive", dismiss: "Dismiss",
      mediaWord: "media", collectionsWord: "collections",
      restoreStarted: "Backup started — refreshing shortly",
      restoreTestStarted: "Restore test started",
      restoreVerified: "Restore verified — fully recoverable",
      restoreRan: "Restore ran — see report",
      restoreFailed: "Restore failed: ",
      alreadyRunning: "A restore test is already running. Please wait.",
      fullyRecoverable: "Fully recoverable:", recordsMatch: "Records match:",
      mediaRestored: "Media restored:", sampleOpened: "Sample opened:",
      missingFiles: "missing file(s) in this backup — not fully recoverable.",
      integrity: "Media integrity:", presentWord: "present", missingWord: "missing", unverifiedWord: "unverified",
      employerVisible: "employer-visible", errorPrefix: "Error:",
      statuses: { active: "active", failed: "failed", incomplete: "incomplete", archived: "archived" },
    },
    adminReqs: {
      title: "Account & Privacy Requests",
      filters: { open: "open", resolved: "resolved", all: "all" },
      sub: "Deletion, export and consent-withdrawal requests are handled manually and audited. No data is removed automatically.",
      noRequests: "No requests", markResolved: "Mark resolved", resolvedWord: "resolved",
      requestResolved: "Request resolved",
      labels: { account_deletion: "Account deletion", data_export: "Data export", consent_withdrawal: "Consent withdrawal" },
    },
  },
};
